import { ArrowRightIcon } from '@heroicons/react/outline';
import Image from 'next/image';
import Link from 'next/link';

import clsxm from '@/lib/clsxm';

import AutoPlayVideo from '@/components/media/AutoPlayVideo';

import { getMediaTemplateAspect, ImageTool } from '@/constants/mediaTemplates';

interface TemplateMasonryProps {
  templates: ImageTool[];
  /** Already-translated strings for a template. */
  getText: (template: ImageTool) => { title: string; description: string };
  getHref?: (template: ImageTool) => string;
  ctaLabel: string;
  /** Cards that stay in the DOM (crawlable link + text) without being shown.
   *  Their media is not mounted, so a hidden card costs no request. */
  isHidden?: (template: ImageTool, index: number) => boolean;
  opensInNewTab?: (template: ImageTool) => boolean;
  columnsClassName?: string;
  className?: string;
}

// Media-first masonry. Every tile reserves its preview's real aspect ratio, so
// portrait clips and images show uncropped and nothing shifts while media
// loads. CSS columns keep it server-renderable with no layout JS.
const TemplateMasonry = ({
  templates,
  getText,
  getHref = (template) => template.uri,
  ctaLabel,
  isHidden,
  opensInNewTab,
  columnsClassName = 'columns-2 lg:columns-3 2xl:columns-4',
  className,
}: TemplateMasonryProps) => (
  <div className={clsxm('gap-3 sm:gap-4', columnsClassName, className)}>
    {templates.map((template, index) => {
      const hidden = isHidden?.(template, index) ?? false;
      const { title, description } = getText(template);

      return (
        <Link
          key={template.slug || template.uri}
          href={getHref(template)}
          {...(opensInNewTab?.(template) && {
            target: '_blank',
            rel: 'noopener noreferrer',
          })}
          style={{ aspectRatio: getMediaTemplateAspect(template) }}
          className={clsxm(
            'group relative mb-3 block w-full break-inside-avoid overflow-hidden rounded-xl bg-gray-100 ring-1 ring-inset ring-black/5 sm:mb-4 dark:bg-slate-800 dark:ring-white/10',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            hidden && 'hidden',
          )}
        >
          {!hidden &&
            (template.isVideoPreview ? (
              <AutoPlayVideo
                src={template.image}
                className='absolute inset-0 h-full w-full object-cover'
              />
            ) : (
              <Image
                src={template.image}
                alt={title}
                fill
                unoptimized={template.image.endsWith('.gif')}
                sizes='(max-width: 1024px) 50vw, (max-width: 1536px) 33vw, 25vw'
                className='object-cover transition-transform duration-500 group-hover:scale-[1.03]'
              />
            ))}

          <div className='pointer-events-none absolute inset-0 bg-black/40 opacity-0 transition-opacity duration-300 sm:group-hover:opacity-100' />
          <div className='pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/80 via-black/25 to-transparent transition-opacity duration-300 group-hover:opacity-100 sm:opacity-80' />

          <div className='absolute inset-x-0 bottom-0 p-3 sm:p-4'>
            <h3 className='text-[13px] font-semibold leading-snug text-white sm:text-[15px]'>
              {title}
            </h3>
            <div className='hidden sm:grid sm:grid-rows-[0fr] sm:opacity-0 sm:transition-all sm:duration-300 sm:group-hover:grid-rows-[1fr] sm:group-hover:opacity-100'>
              <div className='overflow-hidden'>
                <p className='mt-1 line-clamp-2 text-xs leading-relaxed text-white/80'>
                  {description}
                </p>
                <span className='mt-2.5 inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-900'>
                  {ctaLabel}
                  <ArrowRightIcon className='h-3 w-3 rtl:rotate-180' />
                </span>
              </div>
            </div>
          </div>
        </Link>
      );
    })}
  </div>
);

export default TemplateMasonry;
